/**
 * Modelos normalizados de VitalAI.
 *
 * Ninguna capa fuera de `health-normalizer` debe conocer el origen real del
 * dato (HealthKit, Huawei Health, entrada manual, wearables futuros o datos
 * demo). Todo el resto del sistema (baseline, scoring, trends, UI) consume
 * exclusivamente `NormalizedDailyMetrics`.
 */

export type HealthDataSource = 'healthkit' | 'huawei_health' | 'manual' | 'demo';

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface SleepMetrics {
  bedtime: string;
  sleepStart: string;
  wakeTime: string;
  totalMinutes: number;
  awakeMinutes: number;
  coreMinutes: number;
  deepMinutes: number;
  remMinutes: number;
  efficiency: number;
  latencyMinutes: number;
  awakenings: number;
}

export interface RecoveryMetrics {
  hrv: number;
  restingHeartRate: number;
  averageHeartRate: number;
  minHeartRate: number;
  spo2: number;
  respiratoryRate: number;
  /** Desviacion de temperatura respecto al baseline personal, en grados C. */
  temperatureDeviation: number;
}

export interface WorkoutSession {
  id: string;
  type: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  activeCalories: number;
  averageHeartRate?: number;
}

export interface ActivityMetrics {
  steps: number;
  activeCalories: number;
  exerciseMinutes: number;
  distanceKm: number;
  workouts: WorkoutSession[];
}

export interface MealItem {
  id: string;
  name: string;
  mealType: MealType;
  time: string;
  grams?: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sodium: number;
  sugar: number;
  confidence?: number;
  /** Referencia al objeto en R2 (bucket FOOD_PHOTOS), si la comida se registro con foto. */
  photoKey?: string;
}

export interface NutritionMetrics {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sodium: number;
  sugar: number;
  meals: MealItem[];
}

export interface BodyMetrics {
  weightKg?: number;
  bodyTemperature?: number;
}

export interface NormalizedDailyMetrics {
  date: string;
  source: HealthDataSource;
  sleep: SleepMetrics;
  recovery: RecoveryMetrics;
  activity: ActivityMetrics;
  nutrition: NutritionMetrics;
  body: BodyMetrics;
  tags: string[];
}

export type BaselineWindow = 7 | 14 | 30 | 60;

export const BASELINE_WINDOWS: BaselineWindow[] = [7, 14, 30, 60];

export type MetricKey =
  | 'hrv'
  | 'restingHeartRate'
  | 'sleepDuration'
  | 'deepSleep'
  | 'remSleep'
  | 'sleepEfficiency'
  | 'spo2'
  | 'respiratoryRate'
  | 'steps'
  | 'activeCalories'
  | 'exerciseMinutes'
  | 'nutritionCalories'
  | 'proteinIntake'
  | 'weight';

export type TrendDirection = 'up' | 'down' | 'stable';

export interface MetricBaseline {
  metric: MetricKey;
  window: BaselineWindow;
  current: number;
  average: number;
  percentDifference: number;
  trend: TrendDirection;
}

export type DailyMetricBaselines = Partial<Record<BaselineWindow, MetricBaseline>>;

export interface DailyBaselines {
  date: string;
  metrics: Partial<Record<MetricKey, DailyMetricBaselines>>;
}

export interface DailyScores {
  date: string;
  sleepScore: number;
  recoveryScore: number;
  activityScore: number;
  nutritionScore: number;
  readinessScore: number;
}

export type TrendType =
  | 'hrv_declining'
  | 'resting_hr_rising'
  | 'sleep_debt'
  | 'later_bedtime'
  | 'lower_recovery'
  | 'overtraining_signal'
  | 'nutrition_deficit';

export type TrendSeverity = 'info' | 'warning';

export interface DetectedTrend {
  id: string;
  type: TrendType;
  metric: MetricKey;
  severity: TrendSeverity;
  daysActive: number;
  message: string;
}

export const HABIT_TAGS = [
  'gym',
  'cardio',
  'sauna',
  'alcohol',
  'coffee_late',
  'heavy_dinner',
  'stress',
  'travel',
  'sick',
  'nasal_congestion',
  'nap',
  'rest_day',
  'high_workload',
] as const;

export type HabitTag = (typeof HABIT_TAGS)[number];
